/**
 * 容器与节点业务数据转换，供 stores 等公共层使用
 */

import type {
  AgentVideoRatioType,
  CanvasNodeType,
  ContainerStatus,
  ContainerType,
  ContentTag,
  NodeBusinessData,
  NodeData,
  ViduContainerType,
} from '@/app/types';

/**
 * 将单个容器转换为 NodeData
 */
export function convertContainerToNodeData(container: ViduContainerType): NodeData {
  const { id, type, parent_ids = [], status, content } = container;

  const nodeData: NodeData = {
    nodeType: parent_ids.length > 0 ? 'aiCreation' : 'input',
    container_id: id || '',
    container_type: type as ContainerType,
    content_id: content?.id || '',
    status: status as ContainerStatus,
    name: content?.name || id || '',
    tags: (content?.tags as ContentTag) || [],
    parent_ids: parent_ids,
    created_at: container.created_at || '',
  };

  // 根据容器类型填充对应的内容
  if (type === 'text' && content?.text) {
    nodeData.text = content.text;
  } else if (type === 'image' && content?.image) {
    nodeData.images_url = content.image.url || '';
  } else if ((type === 'video' || type === 'clip') && content?.video) {
    nodeData.videos = {
      url: content.video.url || '',
      cover_url: content.video.cover_url || '',
      ratio: content.video.ratio as AgentVideoRatioType,
      bgm_url: content.video.bgm_url || '',
      bgm_volume: (content.video.bgm_volume || 0) < 0 ? 0 : 1, // 背景音量，范围 0-1，小于0的值认为音频关闭设置为0静音，大于等于0的值认为音频开启设置默认值为1
      tts_url: content.video.tts_url || '',
      tts_volume: (content.video.tts_volume || 0) < 0 ? 0 : 1, // 语音音量，范围 0-1，小于0的值认为音频关闭设置为0静音，大于等于0的值认为音频开启设置默认值为1
      input_text: content.video.input_text || '',
      index: container.index || 0,
      duration: content.video.duration || 0,
      reference_image_id: content.video.reference_image_id || [],
      full_narration: content.video.full_narration || '',
      voice_id: content.video.voice_id || '',
      // watermark_uri 仅在 agent 视频内容（LlmAgentAgentV1VideoContent）上返回，ViduV1VideoContent 类型不含该字段
      watermark_url: (content.video as { watermark_uri?: string }).watermark_uri || undefined,
    };
  } else if (type === 'script' && content?.script) {
    nodeData.script = content.script.text || '';
  } else if (type === 'video_group' && content?.video_group) {
    nodeData.video_group = content.video_group;
  }

  return nodeData;
}

/**
 * 根据容器类型和状态确定节点类型
 */
export function getNodeTypeFromContainer(container: ViduContainerType): CanvasNodeType {
  const { type, parent_ids } = container;

  if (parent_ids?.length === 0) {
    return 'inputNode';
  }

  if (type === 'script') {
    return 'creationScriptNode';
  }

  if (type === 'video' || type === 'video_group' || type === 'clip') {
    return 'creationVideoNode';
  }

  return 'inputNode';
}

/**
 * 将单个容器转换为节点业务数据
 * @param container 容器数据
 * @param existingNode 现有节点（用于更新场景，保留某些字段）
 */
export const containerToBusinessData = (container: ViduContainerType): NodeBusinessData => {
  const nodeType = getNodeTypeFromContainer(container);
  const nodeData = convertContainerToNodeData(container);
  return {
    id: container.id || '',
    name: container.content?.name || container.id || '',
    type: nodeType,
    data: nodeData,
  };
};
